import React, { useEffect, useState } from "react";
import { X, Loader2, ExternalLink, Copy, FileWarning } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function NfoViewerModal({ releaseName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!releaseName) return;
    setLoading(true); setErr(null);
    api.get("/nfo", { params: { release: releaseName } })
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.status === 404
        ? "No NFO found for this release on srrdb.com."
        : "Failed to fetch NFO."))
      .finally(() => setLoading(false));
  }, [releaseName]);

  const copy = async () => {
    if (!data?.nfo) return;
    try { await navigator.clipboard.writeText(data.nfo); toast.success("NFO copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="nfo-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="nfo-modal"
        className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="px-5 py-3 border-b border-[var(--tt-border)] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[var(--tt-primary)]/15 border border-[var(--tt-primary)]/30 flex items-center justify-center">
            <FileWarning className="w-4 h-4 text-[var(--tt-primary)]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-base font-extrabold tracking-tight">Release NFO</h3>
            <div className="text-[10px] font-mono-tt text-[var(--tt-text-muted)] truncate">
              {data?.release || releaseName}
            </div>
          </div>
          {data?.nfo && (
            <button
              onClick={copy}
              data-testid="nfo-copy"
              className="p-2 rounded-full hover:bg-white/10 text-[var(--tt-text-muted)] hover:text-white"
              title="Copy NFO"
            >
              <Copy className="w-4 h-4" />
            </button>
          )}
          <button onClick={onClose} data-testid="nfo-close" className="p-2 rounded-full hover:bg-white/10 text-[var(--tt-text-muted)] hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center gap-2 justify-center text-[var(--tt-text-muted)] py-16 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Fetching NFO from srrdb.com…
            </div>
          ) : err ? (
            <div className="m-6 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3" data-testid="nfo-error">
              {err}
            </div>
          ) : data?.nfo ? (
            <pre
              data-testid="nfo-content"
              className="text-[11px] leading-[1.15] text-[#E5E5E5] font-mono whitespace-pre p-5 select-text"
              style={{ fontFamily: "'Cascadia Code', 'JetBrains Mono', 'Consolas', 'Lucida Console', monospace" }}
            >
              {data.nfo}
            </pre>
          ) : null}
        </div>

        {data?.source_url && (
          <div className="px-5 py-3 border-t border-[var(--tt-border)] flex items-center justify-between gap-3 text-[11px] text-[var(--tt-text-faint)] font-mono-tt">
            <span>Source · {data.source}</span>
            <a
              href={data.source_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nfo-source-link"
              className="inline-flex items-center gap-1 text-[var(--tt-primary)] hover:text-white"
            >
              View on srrdb <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
